import React from 'react';
import { message } from 'antd';
import _flatten from 'lodash/flatten';
import { Controller, useController, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import Svg from 'react-inlinesvg';
import { useSignedUser } from 'api/auth';
import { createStore } from 'api/store';
import { CountryCode } from 'api/types/store';
import { StoreSource, useAnalytics } from 'analytics';
import ZipCodeInput from 'components/Form/ZipCodeInput';
import { getFieldError } from 'components/NewProductStepper/utils';
import { URLS } from 'components/Routes/urls';
import { STORE_WALKTHROUGH } from 'components/Onboarding/StoreWalkthrough';
import { Button, PanelModal, TextField } from 'ui-kit';
import { defaultCountry } from 'utils/geo';
import { reverse } from 'utils/urls';
import './QuickSetupModal.scss';

const BASE_CLASS = 'QuickSetupModal';
const FORM_ID = 'quick-setup-form';
const SOURCE = StoreSource.onboarding;
const GENERIC_ERROR = 'Something went wrong. Please try again.';
const ASSETS = `${process.env.PUBLIC_URL}/intro`;
// stars.svg bakes in a mint plate sized for the 100px intro modal; this is the plain glyph
const SPARKLES_ICON = `${ASSETS}/sparkles.svg`;

const PRODUCTS: {
  id: string;
  title: React.ReactNode;
  subtitle?: string;
  icon: string;
  image?: string;
}[] = [
  {
    id: 'check-in',
    // The card is wide enough to fit "Late" on the first line; the comp breaks after the slash
    title: (
      <>
        Early Check-In /<br />
        Late Check-Out
      </>
    ),
    icon: `${ASSETS}/bed.svg`,
    image: `${ASSETS}/f1.jpg`,
  },
  { id: 'pets', title: 'Guest & Pet Fees', icon: `${ASSETS}/paw.svg`, image: `${ASSETS}/f2.jpg` },
  {
    id: 'local',
    title: 'Local Services',
    subtitle: 'Massages, chefs, outings + more',
    icon: `${ASSETS}/lotus.svg`,
    image: `${ASSETS}/f3.jpg`,
  },
];

interface FormValues {
  country: CountryCode;
  postalCode: string;
  storeName: string;
}

const required = (message: string) => ({
  validate: (value: string) => Boolean(value.trim()) || message,
});

export default function QuickSetupModal() {
  const history = useHistory();
  const capture = useAnalytics();
  const { data: user } = useSignedUser();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { country: defaultCountry as CountryCode, postalCode: '', storeName: '' },
  });
  const { field: countryField } = useController({ control, name: 'country' });

  const onSubmit = ({ country, postalCode, storeName }: FormValues) => {
    capture('store_create_submitted', { source: SOURCE });

    return createStore({
      owner: user.owner_id,
      country,
      postalCode: postalCode.trim(),
      storeName: storeName.trim(),
    })
      .then((res) => {
        if (res.ok) {
          capture(
            'store_created',
            { source: SOURCE, store_id: res.data?.id },
            { setOnce: { first_store_created_at: new Date().toISOString() } },
          );
          history.push(reverse({ url: URLS.STORE_DETAILS, params: { storeId: res.data.id } }), {
            startWalkthrough: STORE_WALKTHROUGH,
          });
          return;
        }
        // `errors.fields` is keyed by field name, not indexed.
        const fields = res.errors.fields ?? {};
        capture('store_create_failed', {
          source: SOURCE,
          reason: Object.keys(fields).join(',') || res.errors.nonFields?.[0] || 'unknown',
        });
        message.error(
          res.errors.nonFields?.[0] ?? _flatten(Object.values(fields))[0] ?? GENERIC_ERROR,
        );
      })
      .catch(() => {
        capture('store_create_failed', { source: SOURCE, reason: 'request_failed' });
        message.error(GENERIC_ERROR);
      });
  };

  return (
    <PanelModal
      visible
      closable={false}
      className={BASE_CLASS}
      title={
        <>
          <strong>You’re in!</strong>
          <br />
          Let’s create your first store in three clicks.
        </>
      }
      header={
        <form id={FORM_ID} className={`${BASE_CLASS}-form`} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="postalCode"
            rules={required('ZIP code is required')}
            render={({ field: { ref, ...rest }, fieldState }) => (
              <ZipCodeInput
                label="ZIP code of your first listing"
                required
                fullWidth
                country={countryField.value}
                onCountryChange={countryField.onChange}
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
          <Controller
            control={control}
            name="storeName"
            rules={required('Listing name is required')}
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                label="Name of your listing"
                placeholder="e.g. Cozy Cabin"
                required
                fullWidth
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
        </form>
      }
      footer={
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          className={`${BASE_CLASS}-submit`}
          disabled={!isValid}
          loading={isSubmitting}
        >
          Make my first store
        </Button>
      }
    >
      <div className={`${BASE_CLASS}-panel`}>
        <div className={`${BASE_CLASS}-intro`}>
          <span className={`${BASE_CLASS}-intro__icon`} aria-hidden="true">
            <Svg src={SPARKLES_ICON} />
          </span>
          <div>
            <p className={`${BASE_CLASS}-intro__title`}>
              Your store comes pre-loaded with products and local services.
            </p>
            <p className={`${BASE_CLASS}-intro__note`}>You can turn them on or off at any time.</p>
          </div>
        </div>
        <div className={`${BASE_CLASS}-products`}>
          {PRODUCTS.map(({ id, title, subtitle, icon, image }) => (
            <div key={id} className={`${BASE_CLASS}-product`}>
              <div
                className={`${BASE_CLASS}-product__image`}
                style={image ? { backgroundImage: `url(${image})` } : undefined}
              />
              <div className={`${BASE_CLASS}-product__footer`}>
                <span className={`${BASE_CLASS}-product__icon`} aria-hidden="true">
                  <Svg src={icon} />
                </span>
                <div>
                  <p className={`${BASE_CLASS}-product__title`}>{title}</p>
                  {subtitle && <p className={`${BASE_CLASS}-product__subtitle`}>{subtitle}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PanelModal>
  );
}
